import { useMutation, useQuery } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import CheckCircle from "@mui/icons-material/CheckCircle";
import RemoveIcon from "@mui/icons-material/Remove";
import Restore from "@mui/icons-material/Restore";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  GridLegacy as Grid,
  InputLabel,
  Link,
  List,
  ListItem,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { gql } from "generated-graphql";
import {
  ResetReportsMutation,
  ResetReportsMutationVariables,
  TierIiReportEncampStatus,
  TierIiReportOrgStatus,
  TierIiReportStep,
} from "generated-graphql/graphql";
import { useResetSafetyOverrideAuth } from "hooks/useResetReportAuth";
import pluralize from "pluralize";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { stepToDisplayName } from "routes/Customer/Chemicals/Report/useReport";
import { encampStatusToLabel, orgStatusToLabel } from "util/constants";

import { useAlerts } from "./Alerts/AlertProvider";
import { DataGrid } from "./DataGrid";
import { Dialog } from "./Dialog";
import { ResetReportExplainer } from "./ResetReportExplainer";

type Props = {
  onClose: () => void;
  selectedReportIds: string[];
};

const orgStatuses: TierIiReportOrgStatus[] = [
  TierIiReportOrgStatus.NotStarted,
  TierIiReportOrgStatus.NotReporting,
  TierIiReportOrgStatus.Assigned,
  TierIiReportOrgStatus.InProgress,
  TierIiReportOrgStatus.InReview,
  TierIiReportOrgStatus.Verified,
];

const encampStatuses: TierIiReportEncampStatus[] = [
  TierIiReportEncampStatus.AwaitingVerification,
  TierIiReportEncampStatus.Processing,
  TierIiReportEncampStatus.FilingComplete,
  TierIiReportEncampStatus.NotReporting,
];

const REPORTS_SAFE_TO_RESET = gql(`
  query ReportsSafeToReset($reportIds: [ID!]!) {
    bulkReportsSafeToReset(reportIds: $reportIds) {
      id
      isSafeToReset
      unsafeResetReasons
      activities {
        isSafeToReset
        activity {
          id
          type
        }
      }
      tenant {
        name
      }
      facility {
        name
        state
      }
    }
  }
`);

const RESET_REPORTS = gql(`
  mutation ResetReports($input: BulkResetReportInput!) {
    bulkResetTierIIReports(input: $input) {
        succeeded
    }
  }
`);

export const ResetReportsFormDialog = (props: Props) => {
  const [radioValue, setRadioValue] = useState<UnsafeReportsRadioOptions>(null);
  const [resetReason, setResetReason] = useState("");

  const [orgStatus, setOrgStatus] = useState<TierIiReportOrgStatus>();
  const [encampStatus, setEncampStatus] = useState<TierIiReportEncampStatus>();
  const [shouldForceCancelWorkflow, setShouldForceCancelWorkflow] = useState<boolean>(false); // Default to false - destructive action
  const [shouldForceReSnapshot, setShouldForceReSnapshot] = useState<boolean>(false);
  const isFilingCompleteTarget = encampStatus === TierIiReportEncampStatus.FilingComplete;

  const [reportStepsUntouched, setReportStepsUntouched] = useState(
    Object.keys(stepToDisplayName).map((tierIIReportStep) => ({
      step: tierIIReportStep as TierIiReportStep,
      name: stepToDisplayName[tierIIReportStep as TierIiReportStep],
      value: false,
    }))
  );
  const alerts = useAlerts();
  const { canOverrideSafetyCheck, loading: authLoading } = useResetSafetyOverrideAuth();

  const [resetReportsMutation, { loading: isResettingReports }] = useMutation<
    ResetReportsMutation,
    ResetReportsMutationVariables
  >(RESET_REPORTS, {
    onCompleted() {
      const count = (reportRows ?? []).length;
      alerts.success(`Successfully queued ${count} ${pluralize("report", count)} to be reset.`);
    },
    onError(error) {
      alerts.error("We encountered an error resetting the reports.", error);
    },
  });

  const { data, loading } = useQuery(REPORTS_SAFE_TO_RESET, {
    variables: {
      reportIds: props.selectedReportIds,
    },
    skip: !props.selectedReportIds.length,
    fetchPolicy: "network-only",
  });

  const reportRows = useMemo(() => {
    if (radioValue === "removeUnsafeReports") {
      return data?.bulkReportsSafeToReset.filter((row) => row.isSafeToReset);
    }
    return data?.bulkReportsSafeToReset;
  }, [data?.bulkReportsSafeToReset, radioValue]);

  const safeToReset = useMemo(() => reportRows?.every((row) => row.isSafeToReset), [reportRows]);

  // radioValue being set means user wants to override (either by removing unsafe or overriding safety)
  const isOverridingSafety = radioValue === "overrideSafetyCheck";

  // A reason is always required for any reset
  const hasValidReason = resetReason.trim().length > 0;

  // User can reset if:
  // 1. They provided a reason, AND
  // 2. All reports are safe OR user selected to remove unsafe reports OR user has permission to override
  const canReset = useMemo(
    () =>
      hasValidReason &&
      (safeToReset ||
        radioValue === "removeUnsafeReports" ||
        (isOverridingSafety && canOverrideSafetyCheck)),
    [hasValidReason, safeToReset, radioValue, isOverridingSafety, canOverrideSafetyCheck]
  );

  useEffect(() => {
    if (!isFilingCompleteTarget) {
      return;
    }

    setShouldForceCancelWorkflow(false);
    setShouldForceReSnapshot(false);
  }, [isFilingCompleteTarget]);

  const resetReports = useCallback(async () => {
    const untouchedSteps = reportStepsUntouched
      .filter((item) => item.value)
      .map((item) => item.step);

    await resetReportsMutation({
      variables: {
        input: {
          encampStatus: encampStatus ?? TierIiReportEncampStatus.NotReporting,
          organizationStatus: orgStatus ?? TierIiReportOrgStatus.NotStarted,
          reportIds: reportRows?.map((row) => row.id) ?? [],
          safetyOverride: radioValue === "overrideSafetyCheck",
          safetyOverrideReason:
            radioValue === "overrideSafetyCheck" ? resetReason.trim() : undefined,
          resetReason: resetReason.trim(),
          shouldForceCancelWorkflow,
          shouldForceReSnapshot,
          untouchedSteps,
        },
      },
    });
    props.onClose();
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
  }, [
    resetReportsMutation,
    encampStatus,
    orgStatus,
    radioValue,
    resetReason,
    reportStepsUntouched,
    reportRows,
    shouldForceCancelWorkflow,
    shouldForceReSnapshot,
    props.onClose,
  ]);

  if (loading || authLoading) {
    return null;
  }

  return (
    <Dialog
      open
      onClose={props.onClose}
      title="Reset Reports"
      primaryActionButtonProps={{
        saveText: "Reset reports",
        onClick: resetReports,
        loading: isResettingReports,
        startIcon: <Restore />,
        disabled: !canReset || reportRows?.length === 0 || !orgStatus || !encampStatus,
      }}
    >
      <ResetReportExplainer />
      <Typography marginTop={"2rem"}>
        Bulk reset of reports will queue an asynchronous job to reset the reports. The change may
        not be immediately apparent in the UI. Please check the Jobs table to monitor the progress
        of the job.
      </Typography>
      <Box sx={{ m: "2rem 0" }}>
        {safeToReset ? (
          <SafeResetIndicator />
        ) : (
          <UnsafeResetIndicatorWithRadioOptions
            radioValue={radioValue}
            onChangeRadio={(e) => {
              setRadioValue(e.target.value as UnsafeReportsRadioOptions);
            }}
            canOverrideSafetyCheck={canOverrideSafetyCheck}
          />
        )}
      </Box>

      <TextField
        fullWidth
        required
        multiline
        rows={2}
        label="Reason for reset"
        placeholder="Why are you resetting these reports?"
        value={resetReason}
        onChange={(e) => setResetReason(e.target.value)}
        sx={{ mb: "1rem" }}
        helperText="Please include relevant context such as Slack threads, Linear tickets, or customer requests."
      />

      <Stack direction="row" gap={3}>
        <FormControl fullWidth disabled={!canReset} sx={{ mb: "1rem" }} required>
          <InputLabel id="org-status-label">Org Status</InputLabel>
          <Select
            labelId="org-status-label"
            id="org-status"
            label="Org Status"
            value={orgStatus}
            onChange={(e) => setOrgStatus(e.target.value as TierIiReportOrgStatus)}
          >
            {orgStatuses.map((orgStatus) => (
              <MenuItem key={orgStatus} value={orgStatus}>
                {orgStatusToLabel(orgStatus)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={!canReset} sx={{ mb: "1rem" }} required>
          <InputLabel id="encamp-status-label">Encamp Status</InputLabel>
          <Select
            labelId="encamp-status-label"
            id="encamp-status"
            label="Encamp Status"
            value={encampStatus}
            onChange={(e) => setEncampStatus(e.target.value as TierIiReportEncampStatus)}
          >
            {encampStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                {encampStatusToLabel(status)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <Stack direction="row" marginY={2}>
        <FormControlLabel
          label={"Force Cancel Workflow"}
          disabled={isFilingCompleteTarget}
          control={
            <Checkbox
              checked={shouldForceCancelWorkflow}
              disabled={isFilingCompleteTarget}
              onChange={() => {
                setShouldForceCancelWorkflow((prev) => !prev);
              }}
            />
          }
        />
        <FormControlLabel
          label={"Force Replace Snapshot"}
          disabled={isFilingCompleteTarget}
          control={
            <Checkbox
              checked={shouldForceReSnapshot}
              disabled={isFilingCompleteTarget}
              onChange={() => {
                setShouldForceReSnapshot((prev) => !prev);
              }}
            />
          }
        />
      </Stack>
      {isFilingCompleteTarget ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Reports cannot force cancel workflow or force replace snapshot when setting Encamp Status
          to Filing Complete.
        </Alert>
      ) : null}
      <Box>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography>Set report steps to "untouched"</Typography>
          <Link
            component="button"
            variant="body2"
            onClick={() => {
              setReportStepsUntouched((prev) => prev.map((step) => ({ ...step, value: true })));
            }}
            sx={{
              ...(!canReset && {
                color: "text.disabled",
                pointerEvents: "none",
              }),
            }}
          >
            Check All
          </Link>
        </Stack>
        <Grid container>
          {reportStepsUntouched.map(({ name, value }) => (
            <Grid item sm={4} key={name}>
              <FormControlLabel
                label={name}
                control={
                  <Checkbox
                    disabled={!canReset}
                    checked={value}
                    onChange={() => {
                      setReportStepsUntouched((prev) => {
                        const index = prev.findIndex((step) => step.name === name);
                        const updated = [...prev];
                        updated[index] = {
                          ...prev[index],
                          value: !prev[index].value,
                        };
                        return updated;
                      });
                    }}
                  />
                }
              />
            </Grid>
          ))}
        </Grid>
      </Box>
      <Grid container spacing={1} marginTop={1}>
        <Grid item xs={12}>
          <DataGrid
            columns={[
              {
                field: "tenantName",
                headerName: "Organization",
                valueGetter: (_, row) => row.tenant?.name,
                flex: 1,
              },
              {
                field: "facilityName",
                headerName: "Facility",
                valueGetter: (_, row) => row.facility?.name,
                flex: 0.8,
              },
              {
                field: "facilityState",
                headerName: "State",
                valueGetter: (_, row) => row.facility?.state,
                flex: 0.3,
              },
              {
                field: "isSafeToReset",
                headerName: "Safe to Reset",
                align: "center",
                flex: 0.8,
                renderCell: (params) => {
                  return params.row.isSafeToReset ? <Check /> : <></>;
                },
              },
              {
                field: "unsafeResetReasons",
                headerName: "Unsafe Reset Reasons",
                flex: 1.4,
                align: "center",
                renderCell: (params) => {
                  if (params.row.isSafeToReset) {
                    return <RemoveIcon />;
                  }

                  return (
                    <List disablePadding sx={{ listStyleType: "disc" }}>
                      {params.row.unsafeResetReasons.map((reason) => (
                        <>
                          <ListItem key={reason} disablePadding sx={{ display: "list-item" }}>
                            <Typography fontSize={10}>{reason}</Typography>
                          </ListItem>
                        </>
                      ))}
                    </List>
                  );
                },
              },
            ]}
            getRowHeight={() => "auto"}
            rows={reportRows ?? []}
            disableRowSelectionOnClick
          />
        </Grid>
      </Grid>
    </Dialog>
  );
};

const SafeResetIndicator = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        color: theme.palette.success.main,
        display: "flex",
        alignItems: "end",
      }}
    >
      <CheckCircle />
      <Typography sx={{ marginLeft: "4px" }}>It is safe to reset these reports</Typography>
    </Box>
  );
};

const UnsafeResetIndicator = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        color: theme.palette.error.main,
        display: "flex",
        alignItems: "end",
      }}
    >
      <WarningAmberRounded />
      <Typography fontSize={14} sx={{ marginLeft: "4px" }}>
        It is not safe to reset these reports
      </Typography>
    </Box>
  );
};

type UnsafeReportsRadioOptions = "removeUnsafeReports" | "overrideSafetyCheck" | null;

const UnsafeResetIndicatorWithRadioOptions = (props: {
  onChangeRadio: (event: ChangeEvent<HTMLInputElement>, value: string) => void;
  radioValue: UnsafeReportsRadioOptions;
  canOverrideSafetyCheck: boolean;
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: theme.palette.error.main,
        borderRadius: "3px",
        padding: "8px",
      }}
    >
      <UnsafeResetIndicator />

      <Typography fontSize={14} fontWeight="500" sx={{ m: "1rem 0" }}>
        Making changes now may affect the report submission, which has already started. Some steps
        cannot be recalled, such as submitting a payment.
      </Typography>

      <FormControl>
        <FormLabel id="unsafeReportsOptions">
          <Typography fontSize={14} fontWeight={500}>
            Please choose one of the following options:
          </Typography>
        </FormLabel>
        <Box marginLeft={1}>
          <RadioGroup
            aria-labelledby="unsafeReportsOptions"
            name="controlled-radio-buttons-group"
            value={props.radioValue}
            onChange={props.onChangeRadio}
          >
            <FormControlLabel
              value="removeUnsafeReports"
              control={<Radio size="small" />}
              label={
                <Typography fontSize={14} fontWeight={500}>
                  I want to remove unsafe reports from the selection
                </Typography>
              }
            />
            {props.canOverrideSafetyCheck ? (
              <FormControlLabel
                value="overrideSafetyCheck"
                control={<Radio size="small" />}
                label={
                  <Typography fontSize={14} fontWeight={500}>
                    I've reviewed this with an engineer and want to override the safety check
                  </Typography>
                }
              />
            ) : (
              <Typography
                fontSize={14}
                fontWeight="500"
                sx={{ m: "0.5rem 0", color: theme.palette.text.secondary }}
              >
                If you need to override the safety check, please reach out in{" "}
                <Link href="https://encamp.slack.com/archives/C02KQS2CWQF" target="_blank">
                  #tier-2-basecamp
                </Link>{" "}
                on Slack and tag <strong>@tierii-captains</strong> for assistance.
              </Typography>
            )}
          </RadioGroup>
        </Box>
      </FormControl>
    </Box>
  );
};
